import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { MeasurementPoint, MeasurementPointDetail } from '@core/models/installation.model';
import { InstallationDataSourcesService } from '@core/services/installation-data-sources.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule } from '@ngx-translate/core';
import { channelLabel } from '../../channels';
import { PlacedRefusal, RefusalPlace, placeRefusal } from '../../editor-refusal';
import { EffectiveFromFieldComponent } from '../effective-from-field/effective-from-field.component';

export interface UnbindDialogData {
  installationOid: string;
  pointOid: string;
  // The zone the instant is entered in.
  timeZone: string;
  points: MeasurementPoint[];
}

// Closes with the point as written, or with nothing when cancelled.
@Component({
  selector: 'xemex-unbind-dialog',
  imports: [
    DialogShellComponent,
    EffectiveFromFieldComponent,
    FilledButtonComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './unbind-dialog.component.html',
})
export class UnbindDialogComponent {
  readonly data = inject<UnbindDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<UnbindDialogComponent, MeasurementPointDetail>>(MatDialogRef);
  private readonly service = inject(InstallationDataSourcesService);
  private readonly destroyRef = inject(DestroyRef);

  readonly point = this.data.points.find(point => point.pointOid === this.data.pointOid) ?? null;
  readonly source = this.point?.binding ? channelLabel(this.point.binding.source) : '';
  readonly instant = signal(new Date().toISOString());
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  // Read by the opener once the dialog closes: the point or the installation changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  messageAt(place: RefusalPlace): string | null {
    const refusal = this.refusal();
    return refusal?.place === place ? refusal.messageKey : null;
  }

  unbind(): void {
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .clearBinding(this.data.installationOid, this.data.pointOid, {
        effective_from: this.instant(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: detail => this.dialogRef.close(detail),
        error: (error: unknown) => {
          this.isSaving.set(false);
          this.refusal.set(placeRefusal(toRefusal(error)));
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
